import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Provider details by NPI, backed by Zocdoc's `GET /v1/providers?npis=…`.
 *
 * `POST` rather than `GET` deliberately: the input is a list, and the same body is where the
 * further Zocdoc filters (specialty, location, insurance) will be added without changing the
 * endpoint's shape.
 */
export const zocdocProviderDetails = oc
  .route({ method: 'POST', path: '/v2/zocdoc/provider-details' })
  .input(
    z.object({
      /** Zocdoc NPIs. Duplicates are collapsed before the call. */
      npis: z.array(z.string().min(1)).min(1).max(100),
    }),
  )
  .output(
    z.object({
      ok: z.boolean(),
      providers: z
        .array(
          z.object({
            /**
             * Echoed onto every provider: one NPI can carry several providers, so this is how
             * a caller joins a result back to what it asked for.
             */
            npi: z.string(),
            providerId: z.string(),
            firstName: z.string().optional(),
            lastName: z.string().optional(),
            practiceName: z.string().optional(),
            specialties: z.array(z.string()).optional(),
            /**
             * Zocdoc's `sp_…` specialty ids, aligned with `specialties`. The stable half of the
             * pair — the display names are prose and can be reworded, so a crosswalk should key
             * on these.
             */
            specialtyIds: z.array(z.string()).optional(),
            /**
             * The visit reason Zocdoc defaults this provider to. Carried because
             * `/v1/provider_locations/availability` **requires** a visit reason, so an availability
             * check for one of the locations below has everything it needs from this one response.
             */
            defaultVisitReasonId: z.string().optional(),
            /**
             * Every visit reason the provider supports, as Zocdoc's `pc_…` ids. Any one of these
             * is a valid `visit_reason_id` for an availability read or a published timeslot's
             * `allowed_visit_reason_ids`.
             *
             * Deliberately unbounded to match Zocdoc, but be aware of the size: our own providers
             * return 435 and 808 entries, so a 100-NPI request can carry tens of thousands of
             * strings. Use `visitReasonCount` when only the size matters.
             */
            visitReasonIds: z.array(z.string()).optional(),
            /** Size of `visitReasonIds`, for callers that do not need the list itself. */
            visitReasonCount: z.number().optional(),
            /** Physical locations first, then virtual (telehealth) ones. */
            locations: z
              .array(
                z.object({
                  /**
                   * The composite `pr_…|lo_…`. This is the `provider_location_ids` value the
                   * availability read takes, and the reason locations are returned here at all.
                   */
                  providerLocationId: z.string(),
                  locationName: z.string().optional(),
                  address: z.string().optional(),
                  city: z.string().optional(),
                  state: z.string().optional(),
                  zipCode: z.string().optional(),
                  phoneNumber: z.string().optional(),
                  /** Zocdoc's authority for this location's zone — publishing needs it. */
                  timeZone: z.string().optional(),
                  acceptsPatientInsurance: z.string().optional(),
                  /** `in_network` / `out_of_network` / `self_pay`. */
                  acceptsBookingRequestsFrom: z.array(z.string()).optional(),
                  requiredFields: z.array(z.string()).optional(),
                  /** Zocdoc's own next-open-day for this location, in provider-local time. */
                  firstAvailabilityDate: z.string().optional(),
                  virtual: z.boolean(),
                }),
              )
              .optional(),
          }),
        )
        .optional(),
      /** Requested NPIs Zocdoc returned nothing for — absence, not failure. */
      notFound: z.array(z.string()).optional(),
      /** Set only when the Zocdoc call itself failed. Never carries a response body. */
      status: z.number().optional(),
      requestId: z.string().optional(),
      error: z.string().optional(),
      missingEnv: z.array(z.string()).optional(),
    }),
  )
